/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */
import _has from "lodash/has";
import _pick from "lodash/pick";
import _merge from "lodash/merge";
import * as applicationStatuses from "./applicationStatus";
import formSteps from "./formSteps";
import formStepsFlexService from "./flexService/formSteps";
import { getProductData } from "@/src/services/productService";
import _isObjectLike from "lodash/isObjectLike";
import ApiClient from "@/src/services/apiClient";
import { sanitizePersonData } from "@/src/services/formServiceFunctions";
import { FormServiceBase, FormServiceSaveProps } from "@/src/types/FormServiceBase";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import { Customer } from "@/src/types/Entities/Customer";
import { isFlexServiceProduct } from "@/src/services/advocardService";

type ApplicationStatus = typeof applicationStatuses[keyof typeof applicationStatuses];

class FormService extends FormServiceBase<ApplicationStatus> {
  dataFields = (product: AbstractCase): { [k in ApplicationStatus]?: { [key: string]: string[] } } => ({
    [applicationStatuses.APPLICATION_STATUS_DESCRIPTION]: {
      [product.name]: ["personalTextMessage"],
    },
    [applicationStatuses.APPLICATION_STATUS_DOCUMENTS]: {
      [product.name]: [],
    },
    [applicationStatuses.APPLICATION_STATUS_PERSONAL]: {
      client: ["gender", "givenName", "familyName"],
      customer: ["telephone"],
      "customer.person": ["gender", "givenName", "familyName"],
    },
    [applicationStatuses.APPLICATION_STATUS_INSURANCE]: {
      caseInsurance: ["insurer", "caseReferenceNumber", "insurancePolicyNumber", "policyHolderType"],
      "caseInsurance.policyHolder": ["gender", "givenName", "familyName"],
    },
  });

  save = async ({ formData, applicationStatus, nextApplicationStatus, product, customer, updateMutation }: FormServiceSaveProps<ApplicationStatus>) => {
    const fields = this.dataFields(product)[applicationStatus] || {};

    if (_has(fields, "customer.person") && !!customer.person?.id) {
      let customerPersonData = _pick(formData.customer.person, fields["customer.person"]);
      customerPersonData = sanitizePersonData(customerPersonData);
      await updateMutation.mutateAsync({
        uri: "/people",
        id: customer.person.id,
        data: customerPersonData,
      });
    }

    if (_has(fields, "customer")) {
      const customerData = _pick(formData.customer, fields.customer);
      await updateMutation.mutateAsync({
        uri: "/customers",
        id: customer.id,
        data: customerData,
      });
    }

    if (_has(fields, "client")) {
      const baseClientData = formData.client.person;
      let clientData = _pick(baseClientData, fields.client);
      clientData = sanitizePersonData(clientData);
      await updateMutation.mutateAsync({
        uri: "/people",
        id: product.client.id,
        data: clientData,
      });
    }

    if (_has(fields, "caseInsurance")) {
      let caseInsuranceData = _pick(formData.caseInsurance, fields["caseInsurance"]);
      caseInsuranceData.insurer = _isObjectLike(caseInsuranceData.insurer)
        ? "/insurers/" + caseInsuranceData.insurer.id
        : caseInsuranceData.insurer;
      await ApiClient.patch("/case_insurances/" + product.caseInsurance.id, { body: JSON.stringify(caseInsuranceData) });
    }

    if (_has(fields, "caseInsurance.policyHolder")) {
      const basePolicyHolderData = formData.caseInsurance.policyHolderType === "me" ? formData.customer.person : formData.caseInsurance.policyHolder;
      let policyHolderData = _pick(basePolicyHolderData, fields["caseInsurance.policyHolder"]);
      policyHolderData = sanitizePersonData(policyHolderData);
      await updateMutation.mutateAsync({
        uri: "/people",
        id: product.caseInsurance.policyHolder.id,
        data: policyHolderData,
      });
    }

    let productData: Partial<AbstractCase> = {};
    if (nextApplicationStatus) {
      productData.applicationStatus = nextApplicationStatus;
    }

    if (_has(fields, product.name)) {
      productData = _merge(productData, _pick(formData, fields[product.name]), _pick(formData, fields.product));
    }
    await updateMutation.mutateAsync({ uri: getProductData(product.name, "apiUri"), id: product.id, data: productData });
  };

  prepareFormData = (product: AbstractCase, customer: Customer) => {
    let resultData = _merge({}, product);
    resultData.customer = _merge({}, customer);
    return resultData;
  };

  getAvailableSteps = (formData: Record<string, any>, product: AbstractCase) => {
    let availableSteps = [];

    if (isFlexServiceProduct(product)) {
      availableSteps.push(formStepsFlexService[applicationStatuses.APPLICATION_STATUS_DESCRIPTION]);
      availableSteps.push(formStepsFlexService[applicationStatuses.APPLICATION_STATUS_DOCUMENTS]);
      availableSteps.push(formStepsFlexService[applicationStatuses.APPLICATION_STATUS_PERSONAL]);
      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_SUMMARY]);
    } else {
      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_DESCRIPTION]);
      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_DOCUMENTS]);
      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_PERSONAL]);
      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_INSURANCE]);
    }

    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_DONE]);

    return availableSteps;
  };
}

export default new FormService();
